import { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import Loader from '../../special/Loader';

const MAX_ROWS = 1000;

// Detects the column separator from the first data line (German CSVs
// typically use ';', spreadsheets sometimes export tab-separated files).
function detectDelimiter(text) {
  const firstLine = text.slice(0, text.indexOf('\n') + 1 || text.length);
  const candidates = [';', ',', '\t'];
  let best = ',';
  let bestCount = 0;
  candidates.forEach((candidate) => {
    const count = firstLine.split(candidate).length - 1;
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  });
  return best;
}

// Minimal RFC-4180 style parser: quoted fields, escaped quotes, CRLF.
function parseCsv(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      if (rows.length > MAX_ROWS) break;
    } else if (char !== '\r') {
      field += char;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((cell) => cell !== ''));
}

// UTF-8 first, Windows-1252 as fallback so umlauts in legacy exports survive.
function decodeBuffer(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

const CsvPreview = ({ src }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(false);
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        const text = decodeBuffer(buffer);
        setRows(parseCsv(text, detectDelimiter(text)));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (error)
    return (
      <Box className="w-full h-full flex items-center justify-center opacity-60">
        <Typography variant="body2">{t('Cant load File')}</Typography>
      </Box>
    );
  if (rows === null) return <Loader />;

  const truncated = rows.length > MAX_ROWS;
  const visibleRows = truncated ? rows.slice(0, MAX_ROWS) : rows;
  const [header, ...body] = visibleRows;

  return (
    <Box className="w-full h-full flex flex-col overflow-hidden">
      <Box className="flex-1 min-h-0 overflow-auto">
        <Box
          component="table"
          className="border-collapse w-full"
          sx={{ fontSize: '12px' }}
        >
          {header && (
            <Box component="thead">
              <Box component="tr">
                {header.map((cell, index) => (
                  <Box
                    component="th"
                    key={index}
                    className="text-left px-2 py-1 whitespace-nowrap sticky top-0"
                    sx={{
                      backgroundColor: colors.glassSolid,
                      borderBottom: `1px solid ${colors.glass}`,
                      fontWeight: 600,
                    }}
                  >
                    {cell}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box component="tbody">
            {body.map((cells, rowIndex) => (
              <Box component="tr" key={rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <Box
                    component="td"
                    key={cellIndex}
                    className="px-2 py-1 whitespace-nowrap"
                    sx={{ borderBottom: `1px solid ${colors.glass}` }}
                  >
                    {cell}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {truncated && (
        <Typography
          variant="caption"
          className="px-2 py-1"
          sx={{ opacity: 0.6, borderTop: `1px solid ${colors.glass}` }}
        >
          {t('Preview limited to the first 1000 rows')}
        </Typography>
      )}
    </Box>
  );
};

export default CsvPreview;
